import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import {
  DIVIDEND_TAX_RATES,
  CUSTOM_RATE_CODE,
  type DividendTaxRate,
  type Region,
} from "@/content/dividend-tax-rates";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface FormState {
  initialInvestment: number;
  monthlyContribution: number;
  startingYieldPct: number;
  dividendGrowthPct: number;
  priceGrowthPct: number;
  years: number;
  reinvest: boolean;
  country: string;
  customRatePct: number;
}

interface Labels {
  initial: string;
  monthly: string;
  yield: string;
  divGrowth: string;
  priceGrowth: string;
  years: string;
  reinvest: string;
  country: string;
}

interface Props {
  state: FormState;
  onChange: (next: FormState) => void;
  labels: Labels;
}

const num = (e: React.ChangeEvent<HTMLInputElement>) =>
  e.target.value === "" ? 0 : Number(e.target.value);

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-foreground/80">{label}</span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </label>
);

const inputCls =
  "h-11 px-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

const REGION_ORDER: Region[] = ["europe", "americas", "asiaPacific", "mea", "other"];

const CalculatorForm = ({ state, onChange, labels }: Props) => {
  const { t, i18n } = useTranslation();
  const set = (patch: Partial<FormState>) => onChange({ ...state, ...patch });
  const [open, setOpen] = useState(false);

  // Resolve country display names from the current locale via Intl.
  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      return new Intl.DisplayNames(["en"], { type: "region" });
    }
  }, [i18n.language]);

  const pctFmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      }),
    [i18n.language]
  );

  const nameFor = (code: string) => {
    try {
      return regionNames.of(code) ?? code;
    } catch {
      return code;
    }
  };

  const grouped = useMemo(() => {
    const buckets: Record<Region, DividendTaxRate[]> = {
      europe: [],
      americas: [],
      asiaPacific: [],
      mea: [],
      other: [],
    };
    for (const c of DIVIDEND_TAX_RATES) buckets[c.region].push(c);
    for (const r of REGION_ORDER) {
      buckets[r].sort((a, b) => nameFor(a.code).localeCompare(nameFor(b.code), i18n.language));
    }
    return buckets;
  }, [i18n.language, regionNames]);

  const selectedLabel =
    state.country === CUSTOM_RATE_CODE
      ? t("calculator.form.customRateOption")
      : `${nameFor(state.country)} · ${pctFmt.format(
          DIVIDEND_TAX_RATES.find((c) => c.code === state.country)?.rate
            ? (DIVIDEND_TAX_RATES.find((c) => c.code === state.country)!.rate) * 100
            : 0
        )}%`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label={labels.initial}>
        <input
          type="number"
          min={0}
          className={inputCls}
          value={state.initialInvestment}
          onChange={(e) => set({ initialInvestment: num(e) })}
        />
      </Field>
      <Field label={labels.monthly}>
        <input
          type="number"
          min={0}
          className={inputCls}
          value={state.monthlyContribution}
          onChange={(e) => set({ monthlyContribution: num(e) })}
        />
      </Field>
      <Field label={labels.yield}>
        <input
          type="number"
          step={0.1}
          min={0}
          className={inputCls}
          value={state.startingYieldPct}
          onChange={(e) => set({ startingYieldPct: num(e) })}
        />
      </Field>
      <Field label={labels.divGrowth}>
        <input
          type="number"
          step={0.1}
          className={inputCls}
          value={state.dividendGrowthPct}
          onChange={(e) => set({ dividendGrowthPct: num(e) })}
        />
      </Field>
      <Field label={labels.priceGrowth}>
        <input
          type="number"
          step={0.1}
          className={inputCls}
          value={state.priceGrowthPct}
          onChange={(e) => set({ priceGrowthPct: num(e) })}
        />
      </Field>
      <Field label={labels.years} hint={t("calculator.form.yearsHelp") as string}>
        <input
          type="number"
          min={1}
          max={50}
          className={inputCls}
          value={state.years}
          onChange={(e) => set({ years: Math.min(50, Math.max(1, num(e))) })}
        />
      </Field>

      {/* Country combobox */}
      <Field label={labels.country}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              className={cn(inputCls, "flex items-center justify-between gap-2 text-start")}
            >
              <span className="truncate">{selectedLabel}</span>
              <ChevronsUpDown className="h-4 w-4 opacity-60 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[320px]" align="start">
            <Command
              filter={(value, search) => {
                // value = code, but we also stash the display name on the item
                return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
              }}
            >
              <div className="flex items-center border-b px-3">
                <Search className="me-2 h-4 w-4 shrink-0 opacity-50" />
                <CommandInput
                  placeholder={t("calculator.form.searchCountry") as string}
                  className="h-10 border-0 focus:ring-0"
                />
              </div>
              <CommandList className="max-h-72">
                <CommandEmpty>{t("calculator.form.noResults")}</CommandEmpty>

                <CommandGroup heading={t("calculator.regions.other") as string}>
                  <CommandItem
                    value={`${t("calculator.form.customRateOption")} custom`}
                    onSelect={() => {
                      set({ country: CUSTOM_RATE_CODE });
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "me-2 h-4 w-4",
                        state.country === CUSTOM_RATE_CODE ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {t("calculator.form.customRateOption")}
                  </CommandItem>
                </CommandGroup>

                {REGION_ORDER.filter((r) => grouped[r].length > 0).map((region) => (
                  <CommandGroup key={region} heading={t(`calculator.regions.${region}`) as string}>
                    {grouped[region].map((c) => {
                      const dn = nameFor(c.code);
                      return (
                        <CommandItem
                          key={c.code}
                          value={`${dn} ${c.code}`}
                          onSelect={() => {
                            set({ country: c.code });
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "me-2 h-4 w-4",
                              state.country === c.code ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="flex-1">{dn}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {pctFmt.format(c.rate * 100)}%
                            {c.isEstimate ? ` · ${t("calculator.form.estLabel")}` : ""}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </Field>

      {state.country === CUSTOM_RATE_CODE && (
        <Field label={t("calculator.form.customRateLabel")}>
          <input
            type="number"
            min={0}
            max={60}
            step={0.1}
            className={inputCls}
            value={state.customRatePct}
            onChange={(e) => set({ customRatePct: Math.min(60, Math.max(0, num(e))) })}
          />
        </Field>
      )}

      <label className="flex items-center gap-3 mt-2 md:mt-7">
        <input
          type="checkbox"
          className="w-5 h-5 accent-primary"
          checked={state.reinvest}
          onChange={(e) => set({ reinvest: e.target.checked })}
        />
        <span className="text-sm font-medium text-foreground/80">{labels.reinvest}</span>
      </label>
    </div>
  );
};

export default CalculatorForm;
